<template>
  <v-dialog v-model="aperto" width="620" scrollable persistent>
    <template v-slot:activator="{ on }">
      <slot name="activator" :on="on" />
    </template>

    <v-card>
      <!-- Passo 1: ricerca del prodotto -->
      <template v-if="!prodotto">
        <v-text-field
          ref="ricerca"
          v-model="testo"
          prepend-inner-icon="mdi-magnify"
          color="grey"
          filled
          :placeholder="segnaposto"
          hide-details
          @keydown.esc="chiudi"
        >
          <template v-slot:append>
            <v-btn outlined class="mt-n1" color="grey" @click="chiudi">Esc</v-btn>
          </template>
        </v-text-field>

        <v-card-text>
          <div v-if="testo.length < 3" class="text-center py-10 grey--text">
            <v-icon size="80" color="grey lighten-1">
              mdi-clipboard-text-search-outline
            </v-icon>
            <div class="mt-4">Scrivi almeno tre lettere</div>
          </div>
          <div v-else-if="!gruppi.length" class="text-center py-10 grey--text">
            Nessun prodotto trovato.
          </div>
          <v-card
            v-for="gruppo in gruppi"
            v-else
            :key="gruppo.diametro"
            flat
            outlined
            class="my-3"
          >
            <v-card-title class="text-subtitle-2 grey--text">
              Vaso Ø {{ gruppo.diametro }}
            </v-card-title>
            <v-card-text class="pt-0">
              <v-list dense class="ml-n4">
                <RigaProdotto
                  v-for="p in gruppo.prodotti"
                  :key="p.id"
                  :prodotto="p"
                  @selezionato="seleziona"
                />
              </v-list>
            </v-card-text>
          </v-card>
        </v-card-text>
      </template>

      <!-- Passo 2: quantità per colore -->
      <template v-else>
        <v-card-title class="grey--text text-subtitle-1">
          <v-btn icon small class="mr-2" @click="torna_indietro">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          Quantità e varietà
          <v-spacer />
          <v-btn icon @click="chiudi"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text>
          <div class="mb-4">
            <div class="text-h6">{{ nome[0] }}</div>
            <div class="text-caption grey--text">{{ nome[1] }}</div>
          </div>

          <div v-if="caricamento" class="py-8 d-flex justify-center">
            <v-progress-circular indeterminate color="green" />
          </div>

          <v-alert v-else-if="errore" type="error" text dense>
            Non è stato possibile caricare i colori.
            <v-btn small text color="error" @click="carica_colori">Riprova</v-btn>
          </v-alert>

          <div v-else-if="!colori.length" class="py-8 text-center grey--text">
            Questo prodotto non ha varietà disponibili.
          </div>

          <template v-else>
            <div v-for="gruppo in colori_per_serie" :key="gruppo.nome || '—'">
              <div v-if="gruppo.nome" class="text-caption grey--text mt-3 mb-1">
                {{ gruppo.nome }}
              </div>
              <div class="d-flex flex-wrap">
                <div v-for="colore in gruppo.colori" :key="colore.id" class="ma-1">
                  <v-badge
                    color="green"
                    :value="qta_di(colore.id) > 0"
                    :content="qta_di(colore.id)"
                    overlap
                    bordered
                  >
                    <v-avatar
                      size="64"
                      style="cursor: pointer"
                      :class="qta_di(colore.id) > 0 ? 'selezionato' : ''"
                      @click="apri_quantita(colore)"
                    >
                      <v-img v-if="colore.img" :src="colore.img" :alt="colore.nome" />
                      <v-icon v-else>mdi-help</v-icon>
                    </v-avatar>
                  </v-badge>
                  <div class="text-caption text-center nome_colore">
                    {{ colore.nome }}
                  </div>
                </div>
              </div>
            </div>
          </template>
        </v-card-text>

        <v-card-actions>
          <span class="ml-2 grey--text text-caption">
            {{ totale_pezzi }} pezzi su {{ colori_scelti }} varietà
          </span>
          <v-spacer />
          <v-btn text color="grey" @click="chiudi">Annulla</v-btn>
          <v-btn outlined color="green" :disabled="!totale_pezzi" @click="conferma">
            <v-icon left>mdi-check</v-icon>Aggiungi
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>

    <!-- Quantità del colore selezionato -->
    <v-dialog v-model="dialog_qta" width="280">
      <v-card v-if="colore_attivo">
        <v-card-title class="text-subtitle-1">{{ colore_attivo.nome }}</v-card-title>
        <v-card-text>
          <v-text-field
            ref="campo_qta"
            v-model="qta_bozza"
            type="number"
            outlined
            dense
            hide-details
            autofocus
            @keydown.enter="applica_quantita"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text small color="grey" @click="dialog_qta = false">Annulla</v-btn>
          <v-btn text small color="green" @click="applica_quantita">Ok</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import resolver from "@/service/varieta_resolver";
import RigaProdotto from "@/components/Ordini/desktop/crea_nuovo_ordine/NuovoProdottoRicerca.vue";

/**
 * Selezione di un prodotto e delle quantità per varietà.
 *
 * Presentazionale: non conosce nessuno store e non salva nulla. Emette
 * `conferma` con il prodotto e le voci scelte, e chi lo usa decide come
 * persistirle — nei crisantemi finiscono nel buffer di scrittura, quindi una
 * sola richiesta invece di una per voce.
 *
 * Le voci arrivano da varieta_resolver, che copre sia le varietà di specie
 * sia i colori embedded dei prodotti senza specie.
 */
export default {
  components: { RigaProdotto },

  props: {
    // Restringe il catalogo ricercabile. Di default esclude i crisantemi,
    // che nella prenotazione hanno già la loro griglia.
    filtro: {
      type: Function,
      default: null,
    },
    segnaposto: { type: String, default: "Cerca prodotto..." },
    soloDisponibili: { type: Boolean, default: false },
  },

  data() {
    return {
      aperto: false,
      testo: "",
      prodotto: null,
      colori: [],
      quantita: {},
      caricamento: false,
      errore: false,
      dialog_qta: false,
      colore_attivo: null,
      qta_bozza: "0",
    };
  },

  computed: {
    candidati() {
      const tutti = this.$store.getters.get_lista_prodotti_raw;
      return this.filtro ? tutti.filter(this.filtro) : tutti;
    },

    gruppi() {
      if (this.testo.length < 3) return [];
      const q = this.testo.toLowerCase();
      const trovati = this.candidati.filter(
        (p) =>
          (p.nome && p.nome.toLowerCase().includes(q)) ||
          (p.cod && p.cod.toLowerCase().includes(q))
      );
      const per_diametro = {};
      trovati.forEach((p) => {
        const d = p.diametro || "—";
        if (!per_diametro[d]) per_diametro[d] = [];
        per_diametro[d].push(p);
      });
      return Object.keys(per_diametro)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map((d) => ({ diametro: d, prodotti: per_diametro[d] }));
    },

    nome() {
      return this.prodotto ? this.prodotto.nome.split(" in ") : ["", ""];
    },

    /* Raggruppate per colore: due varietà dello stesso colore vanno
       confrontate fra loro, ed è lì che il nome fa la differenza. */
    colori_per_serie() {
      const per_colore = {};
      this.colori.forEach((c) => {
        const chiave = c.nome_colore || "";
        if (!per_colore[chiave]) per_colore[chiave] = { nome: chiave, colori: [] };
        per_colore[chiave].colori.push(c);
      });
      return Object.values(per_colore).sort((a, b) =>
        a.nome === "" ? -1 : b.nome === "" ? 1 : a.nome.localeCompare(b.nome)
      );
    },

    totale_pezzi() {
      return Object.values(this.quantita).reduce((t, n) => t + n, 0);
    },

    colori_scelti() {
      return Object.values(this.quantita).filter((n) => n > 0).length;
    },
  },

  watch: {
    aperto(adesso) {
      if (adesso) {
        this.$nextTick(() => {
          const campo = this.$refs.ricerca;
          if (campo) campo.focus();
        });
      } else {
        this.reimposta();
      }
    },
  },

  methods: {
    apri() {
      this.aperto = true;
    },

    chiudi() {
      this.aperto = false;
    },

    reimposta() {
      this.testo = "";
      this.prodotto = null;
      this.colori = [];
      this.quantita = {};
      this.errore = false;
    },

    torna_indietro() {
      this.prodotto = null;
      this.colori = [];
      this.quantita = {};
    },

    seleziona(id) {
      this.prodotto = this.candidati.find((p) => p.id === id) || null;
      this.quantita = {};
      if (this.prodotto) this.carica_colori();
    },

    async carica_colori() {
      this.errore = false;
      this.caricamento = true;
      try {
        this.colori = await resolver.carica_voci(this.prodotto, {
          solo_disponibili: this.soloDisponibili,
        });
      } catch {
        this.errore = true;
      } finally {
        this.caricamento = false;
      }
    },

    qta_di(id_colore) {
      return this.quantita[id_colore] || 0;
    },

    apri_quantita(colore) {
      this.colore_attivo = colore;
      this.qta_bozza = String(this.qta_di(colore.id) || "");
      this.dialog_qta = true;
      this.$nextTick(() => {
        const campo = this.$refs.campo_qta;
        const input = campo && campo.$el.querySelector("input");
        if (input) input.select();
      });
    },

    applica_quantita() {
      const n = Number(this.qta_bozza);
      const valida = !isNaN(n) && n > 0 ? n : 0;
      // Sostituzione dell'oggetto: reattività su chiavi nuove in Vue 2.
      const aggiornate = { ...this.quantita };
      if (valida) aggiornate[this.colore_attivo.id] = valida;
      else delete aggiornate[this.colore_attivo.id];
      this.quantita = aggiornate;
      this.dialog_qta = false;
    },

    conferma() {
      const per_id = new Map(this.colori.map((c) => [c.id, c]));
      this.$emit("conferma", {
        prodotto: this.prodotto,
        // `tipo` viaggia con la voce: dice sotto quale chiave va salvata.
        colori: Object.keys(this.quantita).map((id_voce) => ({
          id_voce,
          tipo: per_id.has(id_voce) ? per_id.get(id_voce).tipo : "varieta",
          qta: this.quantita[id_voce],
        })),
      });
      this.chiudi();
    },
  },
};
</script>

<style scoped>
.selezionato {
  border: 2px solid #4caf50;
}
.nome_colore {
  max-width: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
